import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon, ZoomInIcon } from 'lucide-react';
import { interiorPages } from '../../data/samples';
import type { InteriorPage } from '../../types/content';
import { PurchaseButton } from '../PurchaseButton';
export function InsideGuides() {
  const [zoom, setZoom] = useState<number | null>(null);
  const total = interiorPages.length;
  useEffect(() => {
    if (zoom === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoom(null);
      if (e.key === 'ArrowRight') setZoom(z => z === null ? z : (z + 1) % total);
      if (e.key === 'ArrowLeft') setZoom(z => z === null ? z : (z - 1 + total) % total);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [zoom, total]);
  const featured = interiorPages.slice(0, 2);
  const more = interiorPages.slice(2);
  return <section id="inside" className="scroll-mt-4 bg-ivoryDeep" data-id="element-114" data-mp="303qed2yihtcw">
      <div className="mx-auto max-w-site px-5 py-20 md:px-8 lg:py-28" data-id="element-115" data-mp="2lfvp5wjpq74k">
        <div className="grid gap-6 lg:grid-cols-12 lg:gap-14" data-id="element-116" data-mp="2ldsyrbenebzb">
          <div className="lg:col-span-6" data-id="element-117" data-mp="2qdx6od1hpydx">
            <p className="font-mono text-[12px] uppercase tracking-[0.16em] text-copperDeep" data-id="element-118" data-mp="2qcm0y7ohigk6">Inside the Guides</p>
            <h2 className="mt-4 font-display text-[32px] font-bold leading-[1.1] text-ink lg:text-[42px]" data-id="element-119" data-mp="2heaf0iykc6y2">Look inside Diesel Fault Atlas.</h2>
          </div>
          <p className="text-[17px] leading-[1.7] text-inkSoft lg:col-span-6 lg:self-end lg:text-[18px]" data-id="element-120" data-mp="22ikn646p4zlf">
            Explore the visual references, testing sequences, system diagrams, and diagnostic case layouts used throughout the collection. Select any
            page to view it at full size.
          </p>
        </div>

        <div className="mt-12 grid gap-8 md:grid-cols-2 lg:gap-10" data-id="element-121" data-mp="5mdgndfbhyuc">
          {featured.map((p, i) => <PageFigure key={p.src} page={p} onOpen={() => setZoom(i)} eager data-id="element-122" data-mp="457ttc9bmy14" data-mpi={i} />)}
        </div>
        <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3 lg:gap-10" data-id="element-123" data-mp="3qesj2x9k7pcu">
          {more.map((p, i) => <PageFigure key={p.src} page={p} onOpen={() => setZoom(i + 2)} data-id="element-124" data-mp="3mdp2g8jotpak" data-mpi={i} />)}
        </div>
        <div className="mt-14 flex flex-col items-start gap-4 border-t border-rule pt-10 sm:flex-row sm:items-center sm:justify-between" data-id="element-125" data-mp="myld2k3o9g0w">
          <p className="max-w-[520px] text-[17px] leading-[1.6] text-ink" data-id="element-126" data-mp="hzijpwx20y8r">
            Six volumes and five supporting resources built in this visual format.
          </p>
          <PurchaseButton mode="scroll" data-id="element-127" data-mp="97gk20ut7lzj" />
        </div>
      </div>

      <AnimatePresence data-id="element-128" data-mp="15ojmp4n4bh6n">
        {zoom !== null && <motion.div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-charcoal/95 p-4 md:p-8" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={() => setZoom(null)} role="dialog" aria-modal="true" aria-label={interiorPages[zoom].caption} data-id="element-129" data-mp="15q4hmvdch251">
            <motion.img key={zoom} src={interiorPages[zoom].src} alt={interiorPages[zoom].alt} className="max-h-[84vh] w-auto max-w-full rounded-[2px] bg-white" initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} onClick={e => e.stopPropagation()} data-id="element-130" data-mp="2xkm8thw8bohn" />
            <p className="mt-4 text-center text-[15px] text-ivory" data-id="element-131" data-mp="3gcok0dq3qcki">
              {interiorPages[zoom].caption} <span className="ml-2 font-mono text-[12px] text-mist" data-id="element-132" data-mp="3l83tyukznobn">{zoom + 1} / {total}</span>
            </p>
            <button type="button" onClick={() => setZoom(null)} aria-label="Close" className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-ivory hover:bg-white/10" data-id="element-133" data-mp="3cmaycfk2ul4r">
              <XIcon className="h-5 w-5" data-id="element-134" data-mp="3hd13pssi6nzg" />
            </button>
            <button type="button" aria-label="Previous page" onClick={e => {
          e.stopPropagation();
          setZoom((zoom - 1 + total) % total);
        }} className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-charcoal/60 text-ivory hover:bg-white/10" data-id="element-135" data-mp="3hdj8xex58cji">
              <ChevronLeftIcon className="h-5 w-5" data-id="element-136" data-mp="ivdobtc5mem7" />
            </button>
            <button type="button" aria-label="Next page" onClick={e => {
          e.stopPropagation();
          setZoom((zoom + 1) % total);
        }} className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-charcoal/60 text-ivory hover:bg-white/10" data-id="element-137" data-mp="itkhacxmpsdo">
              <ChevronRightIcon className="h-5 w-5" data-id="element-138" data-mp="chhwbzvw62mc" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </section>;
}
function PageFigure({
  page,
  onOpen,
  eager = false
}: {
  page: InteriorPage;
  onOpen: () => void;
  eager?: boolean;
}) {
  return <figure data-id="element-139" data-mp="194mhbtxqf07a">
      <button type="button" onClick={onOpen} aria-label={`View full page: ${page.caption}`} className="group relative block w-full overflow-hidden rounded-[2px] border border-rule bg-white shadow-[0_10px_30px_rgba(21,24,23,0.10)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-graphite" data-id="element-140" data-mp="197ijxvkiyd22">
        <img src={page.src} alt={page.alt} className="block h-auto w-full" loading={eager ? 'eager' : 'lazy'} draggable={false} data-id="element-141" data-mp="1rx6wk8kid6sm" />
        <span className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-[3px] bg-graphite px-2.5 py-1.5 text-[13px] font-medium text-ivory opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-visible:opacity-100" data-id="element-142" data-mp="1xqas7m44vn4d">
          <ZoomInIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-143" data-mp="1nrl6fi949iiu" /> View full page
        </span>
      </button>
      <figcaption className="mt-3 flex items-center gap-3 text-[16px] font-medium text-ink" data-id="element-144" data-mp="1iubbxo6vego5">
        <span className="h-px w-6 bg-copperDeep" aria-hidden="true" data-id="element-145" data-mp="1e10ci84e1269" />
        {page.caption}
      </figcaption>
    </figure>;
}